import api from '../../../app/api';
import { PaginatedList } from '../../../shared/models/paginated-list';
import { transformTraceLogsResponse } from '../../../shared/utils/transform-trace-logs-response';
import { CustomerFile } from '../models/customer-file';
import { CustomerFileParams } from '../models/customer-file-params';
import { CreateCustomerSignUrlParams } from '../models/create-customer-sign-url-params';
import { CustomerMutationParams } from '../models/customer-mutation-params';
import { CustomerParams } from '../models/customer-params';
import { CustomerSignUrlResponse } from '../models/customer-sign-url-response';
import { Customer } from '../models/customer';
import { TraceLogs } from '../../../shared/models/trace-logs';

export const customersApi = api
  .enhanceEndpoints({
    addTagTypes: ['Customers', 'Customer', 'CustomerTraceLogs', 'TraceLogs', 'ClientGroups'],
  })
  .injectEndpoints({
    endpoints: builder => ({
      getCustomers: builder.query<PaginatedList<Customer>, CustomerParams>({
        query: params => ({
          url: '/portal-api/v1/customers',
          params,
        }),
        providesTags: ['Customers'],
      }),
      getCustomer: builder.query<Customer, string>({
        query: id => ({
          url: `/portal-api/v1/customers/${id}`,
        }),
        providesTags: result => (result?.id ? [{ type: 'Customer', id: result.id }] : []),
      }),
      createCustomer: builder.mutation<Customer, Omit<CustomerMutationParams, 'id'>>({
        query: body => ({
          url: '/portal-api/v1/customers',
          method: 'POST',
          body,
        }),
        invalidatesTags: ['Customers', 'TraceLogs', 'ClientGroups'],
      }),
      updateCustomer: builder.mutation<Customer, CustomerMutationParams>({
        query: body => ({
          url: `/portal-api/v1/customers/${body.id}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: result =>
          result?.id ? ['Customers', 'ClientGroups', { type: 'Customer', id: result.id }] : [],
      }),
      deleteCustomer: builder.mutation<void, Customer['id']>({
        query: id => ({
          url: `/portal-api/v1/customers/${id}`,
          method: 'DELETE',
        }),
        invalidatesTags: (_result, _error, id) => ['Customers', { type: 'Customer', id }],
      }),
      createCustomerSignUrl: builder.mutation<CustomerSignUrlResponse, CreateCustomerSignUrlParams>({
        query: ({ customerId, ...body }) => ({
          url: `/portal-api/v1/customers/${customerId}/sign-url`,
          method: 'POST',
          body,
        }),
        invalidatesTags: ['Customers'],
      }),
      uploadCustomerFiles: builder.mutation<CustomerFile, CustomerFileParams>({
        query: ({ customerId, ...body }) => ({
          url: `/portal-api/v1/customers/${customerId}/file-url`,
          method: 'POST',
          body,
        }),
      }),
      getCustomerTraceLogs: builder.query<PaginatedList<TraceLogs>, { id: string; page: number }>({
        query: ({ id, ...params }) => ({
          url: `/portal-api/v1/customers/${id}/trace-logs`,
          params,
        }),
        providesTags: ['CustomerTraceLogs'],
        transformResponse: transformTraceLogsResponse,
      }),
    }),
    overrideExisting: false,
  });

export const {
  useGetCustomerQuery,
  useLazyGetCustomerQuery,
  useGetCustomersQuery,
  useLazyGetCustomersQuery,
  useCreateCustomerMutation,
  useUpdateCustomerMutation,
  useDeleteCustomerMutation,
  useUploadCustomerFilesMutation,
  useCreateCustomerSignUrlMutation,
  useGetCustomerTraceLogsQuery,
} = customersApi;
